import MuiButton, { ButtonProps } from '@mui/material/Button'
import { Casing, TEXT_ROLE_CASING, formatCase } from '@common/helpers/casing'
import cn from 'classnames'
import Icon from '../Icon'
import { formatChildren } from '../Text'
import styles from './Button.module.scss'

/**
 * ============================================================================
 * BUTTON COLOR CONVENTION (the app-wide standard — this is where it lives)
 * ============================================================================
 *
 * There is exactly ONE affirmative primary action per view, and it is BLACK.
 * Everything else follows from that. When picking a button, match the ROLE to
 * the component/variant below — never hand-pick a color:
 *
 *   • BLACK, filled  → the SINGLE primary affirmative action (Save / Submit /
 *     Confirm / Apply). This is the default `<Button>` — no props needed. Maps
 *     to `--action-primary` (near-black) via `.root` in Button.module.scss.
 *
 *   • BLUE (accent)  → "add" and accent/link actions only (e.g. the `+` add
 *     control, text links). Use `<Button add />`, which renders the `+` glyph
 *     for you. Maps to `--action-accent`. Do NOT use blue for a primary submit —
 *     blue is for "add another / navigate", black is for "commit this form".
 *
 *   • NEUTRAL dark icon → utility actions (sort, export, expand, chart toggle).
 *     Rendered by the `IconButton` common component (transparent, neutral).
 *
 *   • OUTLINED → secondary / cancel / dismiss actions. Use `<Button secondary>`
 *     (or `variant="outlined"`). Maps to `--action-primary` as border + text.
 *
 *   • RED → destructive actions (delete / terminate). Use `<Button error>`.
 *     Maps to `--action-danger` / `--status-error-*`.
 *
 * Prop → look mapping enforced below:
 *   (none)      → black filled primary
 *   secondary   → outlined secondary/cancel   (also triggered by variant="outlined")
 *   error       → red destructive             (error + secondary = outlined red)
 *   add         → blue "+" add control        (renders the glyph; needs no children)
 *
 * Canonical usage — let the role pick the look; don't stack MUI `variant` /
 * `color` on top of these props (they're redundant and make the intended color
 * ambiguous to the next developer):
 *   <Button onClick={save}>Save</Button>            // primary (black)
 *   <Button secondary onClick={cancel}>Cancel</Button>   // secondary (outlined)
 *   <Button error onClick={remove}>Delete</Button>       // destructive (red)
 *   <Button add onClick={add} />                         // add (blue "+")
 *
 * Tokens are defined once in `assets/styles/tokens.scss` (light + dark mirror),
 * so both color modes "just work" — never hardcode a color on a button.
 */
interface IProps extends ButtonProps {
  /** Renders the outlined secondary/cancel look (`--action-primary` outline). */
  secondary?: boolean
  /** Renders the red destructive look (`--action-danger`). */
  error?: boolean
  /**
   * Renders the blue "+" add control (`--action-accent`). Supplies the `+` glyph
   * as children when none are given, and defaults `data-cy` to
   * `add-item-button` — the selector ~19 Cypress assertions across 7 page
   * objects use to find an add control. Pass your own `data-cy` to override it.
   */
  add?: boolean
  small?: boolean
  isLoading?: boolean
  withFieldset?: boolean
  isOpen?: boolean
  label?: string
  selected?: boolean
  /** Overrides the button role's case. */
  casing?: Casing
}

const Button = ({
  disabled,
  className,
  secondary,
  error,
  add,
  small,
  classes,
  isLoading = false,
  withFieldset = false,
  isOpen,
  label,
  selected,
  casing = TEXT_ROLE_CASING.button,
  ...props
}: IProps) => {
  // Small is opt-in. `.small` is declared after `.root` and ties with it at
  // (0,1,0), so it wins every property they share — while this defaulted to true
  // a plain `<Button>` was 32px and `.root`'s height was unreachable, which is
  // not what "the default button is --size-md" means. Pass `small` (or
  // `size="small"`) for the 32px toolbar/strip variant.
  const isSmall = small ?? props.size === 'small'

  return (
    <MuiButton
      // Before the spread on purpose: this is a default, and the two call sites
      // with their own `data-cy` (add-legal-entity-button,
      // notes-fullscreen-add-button) must keep winning it.
      data-cy={add ? 'add-item-button' : undefined}
      {...props}
      // Forwarded so the rendered element is genuinely inert: a disabled button
      // is skipped by Tab, ignores Enter, and cannot submit its form. Until now
      // `disabled` only drove a class name, and the sole thing blocking a click
      // was a CSS `pointer-events: none` rule the keyboard never saw.
      disabled={disabled}
      startIcon={!isLoading && props.startIcon}
      endIcon={!isLoading && props.endIcon}
      loading={isLoading}
      classes={{
        root: styles.root,
        loading: styles.loading,
        loadingIndicator: styles.loadingIndicator,
        endIcon: styles.endIcon,
        ...classes,
      }}
      className={cn(className, {
        [styles.disabled]: disabled,
        [styles.secondary]: secondary || props.variant === 'outlined',
        [styles.error]: error,
        [styles.add]: add,
        [styles.small]: isSmall,
      })}
    >
      {withFieldset && (isOpen || selected) && (
        <fieldset
          className={cn(styles.fieldset, {
            [styles.activeBorder]: isOpen,
            [styles.borderSelected]: selected,
          })}
        >
          <legend className={styles.legend}>
            <span>{label && formatCase(TEXT_ROLE_CASING.fieldLabel, label)}</span>
          </legend>
        </fieldset>
      )}
      {add && !props.children ? (
        <Icon name="plus" size="lg" />
      ) : (
        formatChildren(casing, props.children)
      )}
    </MuiButton>
  )
}

export default Button
